import { useEffect, useRef } from 'react';
import { useTree } from '@headless-tree/react';
import { syncDataLoaderFeature, searchFeature, selectionFeature } from '@headless-tree/core';
import { useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';
import {
  configurationTreeQueryKey,
  getConfigurationTree,
} from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import { transformTreeResponse } from '~/settings/components/ConfigurationsPage/helpers/transformConfigResponse';
import {
  getTreeItem,
  getTreeChildrenNodes,
} from '~/settings/components/ConfigurationsPage/helpers/treeDataLoader';

import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

export function useConfigurationTree(mode: 'full' | 'edited' = 'full') {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId);
  const treeDataRef = useRef<ConfigTreeNode | null>(null);
  const currentSearchRef = useRef<string | null>(null);

  const {
    data: treeData,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: configurationTreeQueryKey(accounts, mode, accountsScope),
    queryFn: async () => {
      const response = await getConfigurationTree(api, mode, { accountsScope });
      return transformTreeResponse(response, accounts);
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (treeData) {
      treeDataRef.current = treeData;
    }
  }, [treeData]);

  const tree = useTree<ConfigTreeNode>({
    rootItemId: 'root',
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => {
      const currentData = treeDataRef.current;
      if (!currentData) {
        return false;
      }
      const children = getTreeChildrenNodes(item.getId(), currentData);
      return children.length > 0;
    },
    dataLoader: {
      getItem: (itemId: string) => {
        // Use ref to always get latest treeData
        // syncDataLoaderFeature requires synchronous functions, not promises
        const currentData = treeDataRef.current;
        if (!currentData) {
          return undefined as unknown as ConfigTreeNode;
        }
        return getTreeItem(itemId, currentData) as ConfigTreeNode | undefined;
      },
      getChildren: (itemId: string) => {
        // Use ref to always get latest treeData
        // syncDataLoaderFeature requires synchronous functions, not promises
        const currentData = treeDataRef.current;
        if (!currentData) {
          return [];
        }
        return getTreeChildrenNodes(itemId, currentData);
      },
    } as {
      getItem: (itemId: string) => ConfigTreeNode | Promise<ConfigTreeNode>;
      getChildren: (itemId: string) => string[] | Promise<string[]>;
    },
    features: [syncDataLoaderFeature, searchFeature, selectionFeature],
    initialState: {
      expandedItems: treeData ? [treeData.id] : [],
    },
    // Custom search matching function
    isSearchMatchingItem: (search, item) => {
      const itemName = item.getItemName().toLowerCase();
      const searchLower = search.toLowerCase();
      const matches = itemName.includes(searchLower);
      return matches;
    },
  });

  // Rebuild tree and expand root node when data loads
  useEffect(() => {
    if (treeData && !isLoading) {
      // Rebuild the tree to ensure it picks up the new data
      try {
        tree.rebuildTree();
      } catch (error) {
        console.debug('Tree rebuild:', error);
      }

      // Small delay to ensure tree has processed the data
      const timer = setTimeout(() => {
        try {
          const items = tree.getItems();
          const rootItem = items.find((item) => item.getId() === treeData.id);
          if (rootItem && rootItem.isFolder() && !rootItem.isExpanded()) {
            rootItem.expand();
          }

          // Reapply search if there was one before rebuild
          // Apply after a small delay to ensure tree is ready
          if (currentSearchRef.current) {
            setTimeout(() => {
              const searchValue = currentSearchRef.current;
              if (searchValue && tree.setSearch) {
                tree.setSearch(searchValue);
              }
            }, 50);
          }
        } catch (error) {
          console.debug('Tree expansion:', error);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [treeData, isLoading, tree]);

  // Log errors if any
  useEffect(() => {
    if (error) {
      console.error('Failed to load tree data:', error);
    }
  }, [error]);

  // Intercept setSearch calls to track search state and expand items when needed
  useEffect(() => {
    if (!tree || !tree.setSearch) {
      return;
    }

    // Helper function to expand all items recursively
    // This ensures all instances are loaded so search can find them
    const expandAllItems = (itemId: string) => {
      try {
        const items = tree.getItems();
        const item = items.find((i) => i.getId() === itemId);
        if (item && item.isFolder()) {
          if (!item.isExpanded()) {
            item.expand();
          }
          // Get children from tree data and expand them recursively
          const currentData = treeDataRef.current;
          if (currentData) {
            const children = getTreeChildrenNodes(itemId, currentData);
            // Expand children - the tree will load them when expanded
            children.forEach((childId) => {
              // Use setTimeout to allow tree to load items between expansions
              setTimeout(() => {
                expandAllItems(childId);
              }, 0);
            });
          }
        }
      } catch (error) {
        console.debug('Error expanding item:', error);
      }
    };

    const originalSetSearch = tree.setSearch.bind(tree);
    // Store original for use in rebuild effect
    (tree as { __originalSetSearch?: typeof originalSetSearch }).__originalSetSearch =
      originalSetSearch;

    const wrappedSetSearch = (search: string | null) => {
      currentSearchRef.current = search;

      // If search is active, expand all items so all instances are loaded and searchable
      if (search && search.trim() && treeDataRef.current) {
        // First, rebuild tree to ensure all items are available
        try {
          tree.rebuildTree();
        } catch (error) {
          console.debug('Tree rebuild before search:', error);
        }

        // Expand root node and all children recursively
        const rootId = treeDataRef.current.id;
        expandAllItems(rootId);

        // Longer delay to ensure all items are expanded and loaded before setting search
        // The recursive expansion needs time to complete
        setTimeout(() => {
          // Set search - this will filter the items
          originalSetSearch(search);

          // Rebuild tree again to ensure filtered results are shown
          try {
            tree.rebuildTree();
          } catch (error) {
            console.debug('Tree rebuild after search:', error);
          }
        }, 300);
      } else {
        originalSetSearch(search);
      }
    };

    (tree as { setSearch: typeof originalSetSearch }).setSearch = wrappedSetSearch;
  }, [tree]);

  return { tree, treeData, isLoading, hasData: !!treeData };
}
